"use client";
import { Fragment } from "react";
import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import ChipSerif from "@/components/global/ui/ChipSerif";
import Footer from "@/components/global/partials/Footer";
import Image from "@/helpers/Image";
import { easeOut, motion } from "framer-motion";
import statue from "@/public/event/statue.webp";
import bg from "@/public/event/bg-hero.jpg";
import bgM from "@/public/event/bg-hero-m.png";
import bgMAlt from "@/public/event/hero-bg-m-alt.png";
import bgImage from "@/public/event/bg-image.png";
import bgImageAlt from "@/public/event/bg-image-alt.png";
import x from "@/public/event/x.svg";
import check from "@/public/event/check.svg";
import checkDark from "@/public/event/check-dark.svg";
import grain from "@/public/event/GRAIN.png";
import mask from "@/public/event/mask.png";
import lastBG from "@/public/event/last-bg.jpg";
import atlas from "@/public/event/atlas.webp";
import Countdown from "../global/countdown/Countdown";
import logoWhite from "@/public/logo-white.svg";

export default function PageEventJSX({ data }: { data: PageEventSchema }) {
  return (
    <>
      <main className="bg-[#07171B]">
        {/* Hero Section */}
        <section className="p-[1rem] lg:px-[3rem] lg:pt-[3rem]">
          <div className="flex relative flex-col items-center w-full bg-transparent overflow-clip bg-blend-darken rounded-[1.75rem] h-[92rem] px-[2rem] lg:h-[120.875rem] lg:rounded-[4rem] lg:px-[3rem]">
            <div className="flex absolute justify-between items-center w-[min(146.5rem,100%)] lg:top-[3rem] z-[5] px-[2rem] lg:px-0 top-[2rem]">
              <Image
                src={logoWhite}
                alt=""
                quality={100}
                className="pointer-events-none"
              />

              <div className="flex items-center rounded-full bg-[#FF5F5F]/10 w-fit px-[1.5rem] py-[1rem] gap-[1rem]">
                <div className="rounded-full w-[0.875rem] h-[0.875rem] bg-[#FFBABA]" />
                <p className="m-0 text-[1.75rem] text-[#FFBABA]">
                  {data?.headerChip}
                </p>
              </div>
            </div>
            <div className="absolute bottom-0 w-full bg-gradient-to-b from-transparent opacity-20 h-[42rem] to-[#A3DEB2] z-[2] lg:h-[69.75rem]" />
            <Image
              src={statue}
              alt=""
              quality={100}
              className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 !w-[109.5rem] z-[5]  lg:scale-100 scale-125"
            />

            <Image
              src={bgMAlt}
              alt=""
              quality={100}
              className="pointer-events-none object-cover absolute !h-full lg:hidden !w-full"
            />
            <Image
              src={bg}
              alt=""
              quality={100}
              className="pointer-events-none object-cover absolute w-full !h-full lg:block hidden"
            />

            <div className="absolute bottom-0 left-1/2 rounded-full border border-white/20 -translate-x-1/2 translate-y-[25%] w-[84.375rem] h-[84.375rem]" />

            <div className="relative z-[9] pt-[12rem] lg:pt-[8rem]">
              {data?.heroChip && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="mx-auto rounded-full border px-[1.5rem] w-fit border-[#FFE8BA]/30 mb-[1rem]"
                >
                  <ChipSerif
                    text={data.heroChip}
                    className="m-0 italic text-[#FFE8BA]"
                  />
                </motion.div>
              )}

              {data?.heroText && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.3, delay: 0.075, ease: easeOut }}
                  className="richtext w-[min(98rem,100%)] mb-[3rem] lg:mb-[4rem] prose-h1:text-white prose-p:text-white/70 lg:prose-h1:!text-[7.5rem] prose-h1:!text-[5rem] prose-p:text-[2rem] [&_br]:prose-p:hidden lg:[&_br]:prose-p:block prose-strong:font-[400] prose-strong:text-white prose-strong:pr-[.5rem] lg:prose-strong:pr-0 mx-auto prose-p:w-[min(80rem,100%)] [&_span]:!text-[#A3DEB2] prose-h1:font-[500] prose-h1:font-instrument-sans"
                >
                  <PortableText
                    value={data.heroText}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              {data?.heroCta && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.15,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                  className="mx-auto w-full lg:w-fit"
                >
                  <CTAButton data={data.heroCta} />
                </motion.div>
              )}
            </div>

            <div className="absolute bottom-0 left-0 w-full translate-x-[180%] gap-[7rem] z-[99] max-w-[30rem] -translate-y-[30%] flex-col lg:flex hidden">
              {data?.heroInfos &&
                data?.heroInfos.slice(0, 3).map((item, index) => (
                  <div
                    key={index}
                    className={`${index === 0 ? "translate-x-[25%]" : index === 1 ? "-translate-x-[15%]" : "translate-x-[5%]"}`}
                  >
                    <motion.div
                      initial={{
                        opacity: 0,
                        x: 80,
                        filter: "blur(12px)",
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        filter: "blur(0px)",
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.2,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="flex relative items-center p-[1rem] rounded-[1.5rem] gradient-border-white-opacity gap-[1.5rem] bg-[#07171B]/40 backdrop-blur-[4rem]"
                    >
                      <div className="min-w-[5.25rem] !h-[9rem] bg-[#A3DEB2]/[15%] rounded-[1rem] flex items-center justify-center">
                        <Image
                          src={item.icon}
                          alt=""
                          quality={100}
                          className="pointer-events-none !w-[3rem] !h-[3rem]"
                        />
                      </div>
                      <div className="richtext richtext-start prose-p:m-0 prose-strong:text-white prose-strong:font-[500] prose-p:text-white/70">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    </motion.div>
                  </div>
                ))}
            </div>

            <div
              className={`absolute bottom-0 right-0 w-fit -translate-x-[190%] gap-[12rem] z-[99] -translate-y-[90%] lg:flex hidden flex-col`}
            >
              {data?.heroInfos &&
                data?.heroInfos.slice(3, 5).map((item, index) => (
                  <div
                    key={index}
                    className={`${index === 0 ? "translate-x-[25%] !max-w-[30rem]" : index === 1 ? "translate-x-[15%] !max-w-[35rem]" : ""}`}
                  >
                    <motion.div
                      initial={{
                        opacity: 0,
                        x: -80,
                        filter: "blur(12px)",
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        filter: "blur(0px)",
                      }}
                      transition={{
                        duration: 0.8,
                        delay: 0.3 + index * 0.2,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className={`flex relative items-center p-[1rem] rounded-[1.5rem] gradient-border-white-opacity gap-[1.5rem] bg-[#07171B]/40 backdrop-blur-[4rem] ${index === 1 ? "pr-[2rem]" : ""}`}
                    >
                      <div className="min-w-[5.25rem] !h-[6.5rem] bg-[#A3DEB2]/[15%] rounded-[1rem] flex items-center justify-center">
                        <Image
                          src={item.icon}
                          alt=""
                          quality={100}
                          className="pointer-events-none !w-[3rem] !h-[3rem]"
                        />
                      </div>
                      <div className="richtext richtext-start prose-p:m-0 prose-strong:text-white prose-strong:font-[500] prose-p:text-white/70">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    </motion.div>
                  </div>
                ))}
            </div>
          </div>
          <div className="grid w-full lg:hidden lg:grid-cols-5 gap-[1rem] pt-[1rem]">
            {data?.heroInfos &&
              data?.heroInfos.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                  whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.3 + index * 0.075,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  className="flex relative items-center p-[1rem] rounded-[1.5rem] gradient-border-white-opacity gap-[2rem]"
                  style={{
                    background:
                      "radial-gradient(116.99% 95.96% at 52.76% 0%, rgba(163, 222, 178, 0.10) 0%, rgba(163, 222, 178, 0.00) 100%), rgba(163, 222, 178, 0.15)",
                  }}
                >
                  <div className="w-[7.25rem] h-[7.25rem] bg-[#A3DEB2]/[15%] rounded-[1rem] flex items-center justify-center">
                    <Image
                      src={item.icon}
                      alt=""
                      quality={100}
                      className="pointer-events-none"
                    />
                  </div>
                  <div className="richtext richtext-start prose-p:m-0 prose-strong:text-white prose-strong:font-[500] prose-p:text-white/70">
                    <PortableText
                      value={item.text}
                      components={richTextComponent}
                    />
                  </div>
                </motion.div>
              ))}
          </div>
        </section>

        <section className="relative lg:px-0 lg:border-b border-[#E5E5E5]/10 px-[2rem] py-[10rem] lg:py-[17.25rem]">
          <div className="hidden lg:block">
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 left-[30rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 left-[60rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 left-[90rem] z-[1]" />

            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 right-[30rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 right-[60rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 right-[90rem] z-[1]" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: easeOut }}
            className="w-[min(176.5rem,100%)] lg:p-[3rem] p-[2rem] bg-white/5 lg:rounded-[4rem] rounded-[2rem] mx-auto flex lg:justify-between items-center relative z-[5] backdrop-blur-[4rem] lg:flex-row flex-col gap-[2rem] lg:gap-0"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, delay: 0.075, ease: easeOut }}
              className="w-[min(56.125rem,100%)] lg:pl-[4rem] p-[0.75rem]"
            >
              <div className="richtext richtext-start prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:font-instrument prose-h6:font-[400] prose-h6:tracking-[.5px] prose-h6:leading-[150%] prose-h2:text-white prose-p:font-[300] prose-p:tracking-wide lg:prose-p:tracking-normal prose-p:!text-white/70 prose-p:w-[min(46.625rem,100%)] [&_br]:hidden lg:[&_br]:block [&_span]:text-[#A3DEB2]">
                <PortableText
                  value={data.aboutPropTitle}
                  components={richTextComponent}
                />
              </div>

              <div className="hidden lg:block mt-[2rem]">
                <CTAButton data={data.aboutPropCta} />
              </div>
            </motion.div>

            <div className="w-[min(101.25rem,100%)] flex items-center lg:flex-row flex-col gap-[2rem] lg:gap-0">
              <motion.div
                initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: 0.15,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
                className="w-[min(49.625rem,100%)] lg:p-[4rem] gap-[4rem] flex flex-col p-[2rem] rounded-[2.25rem] relative event-cons-mobile"
              >
                <div className="flex items-center lg:flex-col lg:items-start gap-[2rem] lg:gap-[4rem]">
                  <Image
                    src={data?.aboutPropCons.icon}
                    alt=""
                    quality={100}
                    className="pointer-events-none"
                  />

                  <h3 className="m-0 lg:tracking-normal text-white/80 text-[3rem] font-[400] tracking-[0.025rem] lg:text-[4rem]">
                    {data?.aboutPropCons.title}
                  </h3>
                </div>

                <div className="flex flex-col gap-[1.5rem] lg:gap-[3rem]">
                  {data?.aboutPropCons.items.map((con, index) => (
                    <Fragment key={index}>
                      <div className="flex items-center gap-[1.5rem]">
                        <Image
                          src={x}
                          alt=""
                          quality={100}
                          className="pointer-events-none"
                        />
                        <p className="m-0 lg:tracking-normal text-white/90 font-[200] tracking-[0.035rem] lg:font-[300]">
                          {con.text}
                        </p>
                      </div>

                      {index < data.aboutPropCons.items.length - 1 && (
                        <hr className="w-full border-t lg:hidden border-white/20" />
                      )}
                    </Fragment>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: 0.225,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
                className="w-[min(49.625rem,100%)] lg:p-[4rem] p-[2rem] gap-[4rem] relative rounded-[2.25rem] overflow-clip gradient-border-white-opacity"
                style={{
                  background:
                    "radial-gradient(116.99% 95.96% at 52.76% 0%, rgba(163, 222, 178, 0.32) 0%, rgba(163, 222, 178, 0.00) 100%), rgba(163, 222, 178, 0.15)",
                }}
              >
                <div className="absolute top-0 left-0 w-full h-full">
                  <div className="relative w-full h-full">
                    <Image
                      src={grain}
                      className="pointer-events-none object-cover absolute w-full !h-full"
                      alt=""
                      quality={100}
                    />

                    <Image
                      src={mask}
                      className="object-cover absolute w-full h-full pointer-events-none"
                      alt=""
                      quality={100}
                    />
                  </div>
                </div>

                <div className="flex relative flex-col w-full h-full z-[5] gap-[4rem]">
                  <div className="flex flex-row items-center lg:flex-col lg:items-start gap-[2rem] lg:gap-[4rem]">
                    <div className="relative w-[7.375rem] h-[7.375rem]">
                      <Image
                        src={data?.aboutPropPros.icon}
                        alt=""
                        quality={100}
                        className="pointer-events-none absolute !w-[9rem] h-[11.625rem] lg:left-0 lg:scale-125 scale-110 left-1/2 -translate-x-1/2 lg:translate-x-0 top-1/2 lg:top-0 -translate-y-[65%] lg:translate-y-0"
                      />
                    </div>

                    <h3 className="m-0 text-white lg:pt-0 lg:tracking-normal text-[3rem] font-[400] tracking-[0.025rem] lg:text-[4rem]">
                      {data?.aboutPropPros.title}
                    </h3>
                  </div>

                  <div className="flex flex-col gap-[1.5rem] lg:gap-[3rem]">
                    {data?.aboutPropPros.items.map((con, index) => (
                      <>
                        <div
                          className="flex items-center gap-[1.5rem]"
                          key={index}
                        >
                          <Image
                            src={check}
                            alt=""
                            quality={100}
                            className="pointer-events-none"
                          />
                          <p className="m-0 lg:tracking-normal text-white/90 font-[200] tracking-[0.035rem] lg:font-[300]">
                            {con.text}
                          </p>
                        </div>

                        {index < data.aboutPropPros.items.length - 1 && (
                          <hr className="w-full border-t lg:hidden border-white/20" />
                        )}
                      </>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, delay: 0.3, ease: easeOut }}
              className="w-full lg:hidden lg:pt-[2rem] lg:w-fit"
            >
              <CTAButton data={data.aboutPropCta} />
            </motion.div>
          </motion.div>
        </section>

        <section className="px-[2rem] lg:py-[12.5rem]">
          <div className="relative z-[9]">
            {data?.whatsNextText && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="richtext w-[min(67.375rem,100%)]  prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:tracking-[.5px] prose-h6:font-instrument prose-h6:font-[400] prose-h2:!text-white prose-p:text-white/70 mx-auto [&_span]:!text-[#A3DEB2]"
              >
                <PortableText
                  value={data.whatsNextText}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {data?.whatsNextCta && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, delay: 0.075, ease: easeOut }}
                className="mx-auto w-full mb-[3rem] lg:w-fit lg:mb-[4rem]"
              >
                <CTAButton data={data.whatsNextCta} />
              </motion.div>
            )}

            <div className="w-[min(176.5rem,100%)] grid lg:grid-cols-3 grid-cols-1 mx-auto gap-[2rem] ">
              {data?.whatsNextItems.map((item, index) => {
                const bgColors = ["#85D0A6", "#93BCDB", "#FFF7E2"];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.3,
                      delay: 0.075 + index * 0.075,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                    className="flex relative flex-col p-[1rem] rounded-[3.25rem] lg:rounded-[4rem]"
                    style={{ backgroundColor: bgColors[index] ?? bgColors[0] }}
                  >
                    <div className="relative w-full">
                      <Image
                        src={item.bg}
                        alt=""
                        className="pointer-events-none object-cover !w-full"
                        quality={100}
                      />
                    </div>
                    <div className="flex flex-col flex-1 justify-between p-[2rem] gap-[2rem]">
                      <div className="flex flex-col gap-[2.5rem]">
                        <div className="flex gap-[1rem]">
                          {item.chip &&
                            item.chip.map((chip, index) => (
                              <div
                                className=" bg-[#103037] px-[1rem] !py-[0.75rem] rounded-[0.96875rem] w-fit"
                                key={index}
                              >
                                <p className="m-0 text-white">{chip.text}</p>
                              </div>
                            ))}
                        </div>
                        <div className="richtext richtext-start prose-h4:pt-0 prose-h4:m-0 prose-p:m-0 prose-h4:mb-[3rem] prose-h4:text-[4rem] prose-h4:font-[500]">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      </div>
                      <div className="w-fit py-[2rem] px-[3rem] rounded-[2.75rem] bg-black/10">
                        <p className="m-0">{item.callout.text}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="lg:pt-0 pb-[10rem] px-[2rem] pt-[8rem]">
          {data?.forTradersText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext w-[min(76.75rem,100%)] mb-[3rem] lg:mb-[4rem] prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:font-instrument prose-h6:font-[400] prose-h2:!text-white prose-p:text-white/70 mx-auto [&_span]:text-[#A3DEB2]"
            >
              <PortableText
                value={data.forTradersText}
                components={richTextComponent}
              />
            </motion.div>
          )}

          <div className="grid lg:grid-cols-2 w-[min(176.5rem,100%)] lg:gap-[2rem] mx-auto grid-cols-1 gap-[1rem]">
            <motion.div
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.075,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              className="relative bg-[#93BCDB] overflow-clip p-[1rem] rounded-[2rem] lg:rounded-[3rem] lg:p-[4rem]"
            >
              <Image
                src={data?.forTradersKryptoCard.bg}
                alt=""
                quality={100}
                className="pointer-events-none absolute top-0 right-0 !h-full z-[1] lg:block object-cover hidden"
              />

              <Image
                src={data?.forTradersKryptoCard.bgMobile}
                alt=""
                quality={100}
                className="pointer-events-none !w-full lg:hidden z-[1] object-cover"
              />

              <div className="w-[min(41.5rem,100%)] relative z-[5] p-[2rem] lg:p-0 ">
                <div className="flex lg:flex-col lg:gap-0 lg:mb-0 gap-[2rem] mb-[1.75rem]">
                  <div className="flex-shrink-0 w-[4.5rem] h-[4.5rem] lg:mb-[4rem]">
                    <Image
                      src={data?.forTradersKryptoCard.icon}
                      alt=""
                      quality={100}
                      className="object-cover !w-full h-full pointer-events-none"
                    />
                  </div>
                  <p className="m-0 text-[#103037] text-[3.5rem] font-[600] tracking-[-1.12px] font-instrument-sans lg:mb-[1.75rem]">
                    {data?.forTradersKryptoCard.title}
                  </p>
                </div>
                <div className="flex flex-col gap-[1rem]">
                  {data?.forTradersKryptoCard.items.map((item, index) => (
                    <div className="flex items-center gap-[1.5rem]" key={index}>
                      <Image
                        src={checkDark}
                        alt=""
                        quality={100}
                        className="pointer-events-none shrink-0"
                      />
                      <p className="m-0 lg:text-[2rem] leading-[150%] text-[1.75rem]">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.15,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              className="relative bg-[#FFF7E2] overflow-clip p-[1rem] rounded-[2rem] lg:rounded-[3rem] lg:p-[4rem]"
            >
              <Image
                src={data?.forTradersFuturesCard.bg}
                alt=""
                quality={100}
                className="pointer-events-none absolute top-0 right-0 !h-full z-[1] lg:block hidden object-cover"
              />

              <Image
                src={data?.forTradersFuturesCard.bgMobile}
                alt=""
                quality={100}
                className="pointer-events-none !w-full lg:hidden z-[1] object-cover"
              />

              <div className="w-[min(41.5rem,100%)] relative z-[5] p-[2rem] lg:p-0 ">
                <div className="flex lg:flex-col lg:gap-0 lg:mb-0 gap-[2rem] mb-[1.75rem]">
                  <div className="flex-shrink-0 w-[4.5rem] h-[4.5rem] lg:mb-[4rem]">
                    <Image
                      src={data?.forTradersFuturesCard.icon}
                      alt=""
                      quality={100}
                      className="object-cover !w-full h-full pointer-events-none"
                    />
                  </div>
                  <p className="m-0 text-[#103037] text-[3.5rem] font-[600] tracking-[-1.12px] font-instrument-sans lg:mb-[1.75rem]">
                    {data?.forTradersFuturesCard.title}
                  </p>
                </div>
                <div className="flex flex-col gap-[1rem]">
                  {data?.forTradersFuturesCard.items.map((item, index) => (
                    <div className="flex items-center gap-[1.5rem]" key={index}>
                      <Image
                        src={checkDark}
                        alt=""
                        quality={100}
                        className="pointer-events-none shrink-0"
                      />
                      <p className="m-0 lg:text-[2rem] leading-[150%] text-[1.75rem]">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.225,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              className="flex relative flex-col lg:flex-row lg:col-span-2 lg:justify-between bg-[#85D0A6] p-[3rem] rounded-[2rem] gap-[2rem] lg:rounded-[3rem] lg:p-[4rem]"
            >
              <div className="relative lg:mb-0 z-[5] mb-[2rem]">
                <div className="flex flex-row lg:flex-col lg:gap-0 gap-[2rem]">
                  <div className="flex-shrink-0 w-[4.5rem] h-[4.5rem] lg:mb-[4rem]">
                    <Image
                      src={data?.forTradersMarchLive.icon}
                      alt=""
                      quality={100}
                      className="object-cover !w-full h-full pointer-events-none"
                    />
                  </div>
                  <h3 className="text-[#103037] text-[3.5rem] font-[600] tracking-[-1.12px] font-instrument-sans">
                    {data?.forTradersMarchLive.title}
                  </h3>
                </div>

                <p className="mb-[1rem] text-[2rem] leading-[150%]">
                  {data?.forTradersMarchLive.text}
                </p>
                <div className="flex items-center gap-[1.5rem]">
                  <Image
                    src={checkDark}
                    alt=""
                    quality={100}
                    className="pointer-events-none shrink-0"
                  />
                  <p className="m-0 text-[2rem] leading-[150%]">
                    {data?.forTradersMarchLive.check}
                  </p>
                </div>
              </div>

              <div className="bg-[#07171B]/50 backdrop-blur-[4rem] w-[min(62rem,100%)] lg:rounded-[2rem] rounded-[1.5rem] border border-white/20 shrink-0">
                <div className="prose-h2:text-white prose-h6:text-white/70 [&_*]:border-white/20">
                  <Countdown
                    dateTimeString={data?.countdown}
                    lang={data?.countdownTextLang ?? "de"}
                  />
                </div>
                <div className="w-full border-t py-[2rem] border-white/20">
                  <p className="m-0 text-center text-white">
                    {data?.countdownText}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, delay: 0.3, ease: easeOut }}
            className="mx-auto w-full mt-[4rem] lg:w-fit"
          >
            <CTAButton data={data.forTradersCta} />
          </motion.div>
        </section>

        <section className="relative lg:border-t px-[2rem] pb-[5rem] lg:py-[10rem] lg:border-[#E5E5E5]/10">
          <div className="hidden lg:block">
            <div className="w-full h-[40%] absolute bottom-0 left-0 right-0 bg-gradient-to-b from-transparent to-[#07171B] z-[2]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 left-[30rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 left-[60rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 left-[90rem] z-[1]" />

            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 right-[30rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 right-[60rem] z-[1]" />
            <div className="absolute top-0 h-full w-[.125rem] bg-[#E5E5E5]/10 right-[90rem] z-[1]" />
          </div>

          <div className="w-[min(98.375rem,100%)] lg:border lg:border-[#E5E5E5]/10 rounded-[4rem] lg:p-[4rem] mx-auto bg-[#07171B] relative z-[5]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="richtext richtext-center prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:tracking-[.5px] prose-h6:font-instrument prose-h6:font-[400] prose-h2:text-white [&_span]:!text-[#A3DEB2]"
            >
              <PortableText
                value={data.faqText}
                components={richTextComponent}
              />
            </motion.div>

            <div className="flex flex-col mt-[2rem] gap-[1rem] lg:gap-[2rem]">
              {data?.faqItems.map((item, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                  whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.075 + index * 0.075,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  className="bg-white/5 p-[4rem] rounded-[1.375rem]"
                  key={index}
                >
                  <h4 className="text-white capitalize font-[500] text-[2.5rem] mb-[1.5rem]">
                    {item.question}
                  </h4>

                  <div className="richtext richtext-start prose-p:text-white/70 prose-p:m-0">
                    <PortableText
                      value={item.answer}
                      components={richTextComponent}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative h-[100rem] p-[1rem] lg:p-[3rem] lg:h-[132rem]">
          <div className="relative w-full h-full lg:p-0 overflow-clip rounded-[1.5rem] p-[2rem] lg:rounded-[4rem]">
            <div className="relative z-[9] pt-[4rem] lg:pt-[12.5rem]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="richtext richtext-center prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:font-instrument prose-h6:tracking-[.5px] prose-h6:font-[400] prose-h2:text-white prose-p:!text-white/70 mx-auto w-fit prose-p:text-[2rem] [&_span]:!text-[#A3DEB2]"
              >
                <PortableText
                  value={data.lastText}
                  components={richTextComponent}
                />
              </motion.div>

              {data?.lastCta && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.3, delay: 0.075, ease: easeOut }}
                  className="mx-auto w-full my-[2rem] lg:my-[4rem] lg:w-fit"
                >
                  <CTAButton data={data.lastCta} />
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, delay: 0.15, ease: easeOut }}
                className="bg-[#07171B]/50 backdrop-blur-[2rem] w-[min(62rem,100%)] lg:rounded-[2rem] rounded-[1.5rem] border border-white/20 mx-auto mt-[2rem]"
              >
                <div className="prose-h2:text-white prose-h6:text-[#A3DEB2] [&_*]:border-white/20">
                  <Countdown
                    dateTimeString={data?.countdown}
                    lang={data?.countdownTextLang ?? "de"}
                  />
                </div>
                <div className="w-full border-t py-[2rem] border-white/20">
                  <p className="m-0 text-center text-white">
                    {data?.countdownText}
                  </p>
                </div>
              </motion.div>
            </div>
            <Image
              src={atlas}
              alt=""
              quality={100}
              className="pointer-events-none absolute bottom-0 left-1/2 lg:-translate-x-1/2 z-[2] !w-[116rem] scale-150 lg:scale-100 -translate-y-[25%] lg:translate-y-0 -translate-x-[40%]"
            />
            <Image
              src={lastBG}
              alt=""
              className="pointer-events-none object-cover absolute inset-0 w-full !h-full z-[1]"
              quality={100}
            />
          </div>
        </section>

        {data?.settings?.footer && (
          <Footer
            className="[&_*]:text-white [&_.riskdisclosure_*]:!text-white/50"
            data={data.settings.footer}
            slug={data?.settings?.slug?.current}
          />
        )}
      </main>
    </>
  );
}
